"use client";

import { useState, useEffect } from "react";
import { ChevronRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperType } from "swiper";
import { useAppDispatch } from "@/app/lib/features/redux/redux.hook";
import useProductReview from "@/app/lib/features/productReview/productReview.hook";
import useModal from "@/app/lib/features/modal/modal.hook";
import { getReviewVisuals as getReviewVisualsThunk } from "@/app/lib/features/productReview/productReview.thunk";
import { Button } from "@/app/components/shadcn/button";
import SwiperButton from "@/app/components/elements/buttons/SwiperButton";
import FallbackImage from "@/app/components/elements/FallbackImage";
import PhotoReviewsSkeleton from "@/app/components/elements/skeletons/PhotoReviewsSkeleton";
import { trackGeneric } from "@/app/lib/tracking/interactions";

const PhotoReviews = () => {
  const { createModal } = useModal();
  const dispatch = useAppDispatch();
  const { visualsPhotos, isReviewVisualsLoading, currentProductSlug } = useProductReview();
  
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(true);

  // Fetch visuals on mount
  useEffect(() => {
    if (!currentProductSlug) return;
    
    dispatch(getReviewVisualsThunk({ productSlug: currentProductSlug, page: 1, perPage: 20 }));
  }, [dispatch, currentProductSlug]);

  const updateGradients = (swiper: SwiperType): void => {
    setShowLeftGradient(!swiper.isBeginning);
    setShowRightGradient(!swiper.isEnd);
  };

  const handleShowAllPhotos = () => {
    if (!currentProductSlug) return;

    trackGeneric("review_photos_all", "click");
    createModal(
      "photo-reviews",
      { productSlug: currentProductSlug },
      {
        title: `Fotoğraflı Değerlendirmeler`,
        size: "5xl",
      }
    );
  };

  // Loading state
  if (isReviewVisualsLoading && visualsPhotos.length === 0) {
    return <PhotoReviewsSkeleton />;
  }

  // No photos
  if (visualsPhotos.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg px-6 py-3.5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold">Fotoğraflı Değerlendirmeler</h3>
        <Button
          variant="ghost"
          size="sm"
          className="text-gray-900 hover:text-black"
          onClick={handleShowAllPhotos}
        >
          Tümü
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      <Swiper
        className="relative"
        spaceBetween={8}
        slidesPerView={3}
        onSwiper={updateGradients}
        onSlideChange={updateGradients}
        breakpoints={{
          320: { slidesPerView: 3, spaceBetween: 8 },
          480: { slidesPerView: 4, spaceBetween: 8 },
          640: { slidesPerView: 5, spaceBetween: 8 },
          768: { slidesPerView: 6, spaceBetween: 8 },
          1024: { slidesPerView: 7, spaceBetween: 8 },
          1280: { slidesPerView: 7, spaceBetween: 8 },
          1536: { slidesPerView: 7, spaceBetween: 8 },
        }}
      >
        {visualsPhotos.map((photo, index) => (
          <SwiperSlide key={index}>
            <div className="relative aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity bg-gray-100">
              <FallbackImage
                src={photo}
                alt={`Review photo ${index + 1}`}
                fill
                className="object-cover"
                fallbackClassName="object-contain p-4 opacity-40"
              />
            </div>
          </SwiperSlide>
        ))}
        <div className="absolute z-50 top-1/2 -translate-y-1/2 left-0 w-full items-center justify-between px-3 pointer-events-none hidden xl:flex">
          <SwiperButton position="left" size="sm" slideMode="group" />
          <SwiperButton position="right" size="sm" slideMode="group" />
        </div>
        <div
          className={`absolute top-0 left-0 z-20 w-5 sm:w-8 h-full bg-linear-to-r from-white to-transparent transition-opacity duration-300 pointer-events-none hidden xl:block ${
            showLeftGradient ? "opacity-100" : "opacity-0"
          }`}
        />
        <div
          className={`absolute top-0 right-0 z-20 w-5 sm:w-8 h-full bg-linear-to-l from-white to-transparent transition-opacity duration-300 pointer-events-none hidden xl:block ${
            showRightGradient ? "opacity-100" : "opacity-0"
          }`}
        />
      </Swiper>
    </div>
  );
};

export default PhotoReviews;